/// <amd-module name="VSSUI/Components/Dropdown/Dropdown" />
import * as React from "react";
import { ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { ExpandableButton } from "VSSUI/Button";
import { IExpandable } from "VSSUI/Expandable";
import { ExpandableTextField } from "VSSUI/ExpandableTextField";
import { FilteredListSelection, IListSelection, ISimpleListCell, renderListCell } from "VSSUI/List";
import { getListBoxItemsValue, getUnselectableRanges, IListBoxItem, ListBoxItemType, wrapListBoxItems } from "VSSUI/ListBox";
import { ItemsObserver, Observer } from "VSSUI/Observer";
import * as Resources from "VSSUI/Resources.Dropdown";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { IOffset, IOrigin, IPoint } from "VSSUI/Utilities/Position";
import { getItemsValue } from "VSSUI/Utilities/Provider";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { IDropdownProps, IFilterResults } from "./Dropdown.Props";
import { DropdownCallout } from "./DropdownCallout";
import { IDropdownCalloutProps } from "./DropdownCallout.Props";
import { DropdownExpandableTextField } from "./DropdownExpandableTextField";

interface IDropdownState<T> {
    expanded: ObservableValue<boolean>;
    props: IDropdownProps<T>;
    wrappedItems?: IListBoxItem<T>[];
    filteredSelection: FilteredListSelection;
    filterText: ObservableValue<string>;
    filteredItems: ObservableArray<IListBoxItem<T>>;
}

export class Dropdown<T = {}> extends React.Component<IDropdownProps<T>, IDropdownState<T>> implements IExpandable, IFocusable<{}> {
    public static defaultProps = {
        filterByText: true,
        filterItem: filterItemByText,
        getUnselectableRanges: getUnselectableRanges,
        renderCallout: DropdownCallout,
        renderExpandable: DropdownExpandableTextField,
        renderSelectedItems: renderDropdownSelectedItemText
    };

    public static getDerivedStateFromProps<T>(props: IDropdownProps<T>, state: IDropdownState<T>) {
        if (props.userFilteredItems !== state.props.userFilteredItems || props.items !== state.props.items) {
            updateFilteredItems(props, state);
        }

        return { ...state, props: props, wrappedItems: wrapListBoxItems(props.items) };
    }

    private expandable = React.createRef<ExpandableButton | ExpandableTextField>();
    private expandableContainer = React.createRef<HTMLDivElement>();
    private filterText = new ObservableValue<string>("");
    private parentSelection: IListSelection;
    private timerManagement: TimerManagement;

    constructor(props: IDropdownProps<T>) {
        super(props);

        this.parentSelection = props.selection || new DropdownSelection();
        // string items are wrapped once here.  Only use a string array in the simple case where the items are not changing.
        const wrappedItems = wrapListBoxItems(props.items);
        const itemsValue = getListBoxItemsValue(wrappedItems || props.items);
        this.timerManagement = new TimerManagement();
        this.state = {
            expanded: new ObservableValue<boolean>(false),
            filteredItems: new ObservableArray<IListBoxItem<T>>([...itemsValue]),
            filteredSelection: new FilteredListSelection(this.parentSelection),
            filterText: this.filterText,
            props: props,
            wrappedItems: wrappedItems
        };
    }

    public componentDidMount(): void {
        if (this.props.filterThrottleWait) {
            this.debouncedUpdateFilteredItems = this.timerManagement.debounce(this.debouncedUpdateFilteredItems, this.props.filterThrottleWait);
        }
    }

    public render() {
        const {
            ariaLabel,
            ariaLabelledBy,
            autoSelect,
            className,
            disabled,
            enforceSingleSelect,
            excludeTabStop,
            inputId,
            items,
            placeholder,
            renderExpandable,
            renderSelectedItems,
            role,
            showPrefix,
            required
        } = this.props;
        const selectionObservable = { observableValue: this.parentSelection, filter: this.selectionChanged };

        return (
            <ItemsObserver<IListBoxItem<T> | string>
                getUnselectableRanges={this.props.getUnselectableRanges!}
                items={items}
                selection={this.parentSelection}
            >
                <Observer selection={selectionObservable}>
                    {() => {
                        return renderExpandable!({
                            ariaLabel,
                            ariaLabelledBy,
                            autoSelect,
                            className: css(className, "bolt-dropdown-expandable"),
                            containerRef: this.expandableContainer,
                            disabled,
                            enforceSingleSelect,
                            excludeTabStop,
                            inputId,
                            placeholder,
                            onCollapse: this.onCollapse,
                            onExpand: this.onExpand,
                            expandableRef: this.expandable,
                            renderCallout: this.renderCallout,
                            items: getListBoxItemsValue(this.state.wrappedItems || items),
                            role,
                            renderSelectedItems: renderSelectedItems!,
                            selection: this.parentSelection,
                            showPrefix: showPrefix,
                            required: required
                        });
                    }}
                </Observer>
            </ItemsObserver>
        );
    }

    public collapse = (): void => {
        if (this.expandable.current) {
            this.expandable.current.collapse();
        }
    };

    public expand = (): void => {
        if (this.expandable.current) {
            this.expandable.current.expand();
        }
    };

    public focus() {
        if (this.expandable.current) {
            this.expandable.current.focus();
        }
    }

    private onDismiss = () => {
        if (this.expandable.current) {
            this.expandable.current.collapse();
        }
    };

    private onExpand = () => {
        if (this.props.onExpand) {
            this.props.onExpand();
        }
        this.updateFilteredItems();
        this.state.expanded.value = true;
    };

    private onCollapse = () => {
        if (this.props.onCollapse) {
            this.props.onCollapse();
        }
        this.state.expanded.value = false;
    };

    private onActivate = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<T>) => {
        if (!event.defaultPrevented && event.type === "keydown") {
            const multiSelect = this.props.enforceSingleSelect ? false : this.state.filteredSelection.multiSelect;
            if (multiSelect) {
                this.state.filteredSelection.toggle(
                    this.state.filteredItems.value.indexOf(item),
                    this.state.filteredSelection.alwaysMerge,
                    multiSelect
                );
            } else {
                this.state.filteredSelection.select(
                    this.state.filteredItems.value.indexOf(item),
                    1,
                    this.state.filteredSelection.alwaysMerge,
                    multiSelect
                );
            }
            this.onSelect(event, item);
        }
    };

    private onFilterTextChanged = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
        this.filterText.value = newValue;
        this.debouncedUpdateFilteredItems();
    };

    private onSelect = (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<T>) => {
        const { dismissOnSelect, onSelect } = this.props;
        const selection = this.parentSelection;
        if (onSelect) {
            onSelect(event, item);
        }

        if (
            dismissOnSelect !== undefined
                ? dismissOnSelect
                : selection.value.length > 0 && !(this.props.enforceSingleSelect ? false : selection.multiSelect) && !selection.selectOnFocus
        ) {
            this.onDismiss();
        }
    };

    private selectionChanged = (value: ISelectionRange[], action: string): boolean => {
        this.state.filteredSelection.selectionChanged(value, action);
        return true;
    };

    private renderCallout = (
        dropdown: IExpandable,
        dropdownId: string,
        anchorElement?: HTMLElement,
        anchorOffset?: IOffset,
        anchorOrigin?: IOrigin,
        anchorPoint?: IPoint,
        dropdownOrigin?: IOrigin
    ): JSX.Element => {
        const {
            actions,
            ariaLabel,
            calloutContentClassName,
            columns,
            containerClassName,
            filterPlaceholderText,
            filteredNoResultsText,
            getUnselectableRanges,
            items,
            loading,
            noItemsText,
            onToggle,
            portalProps,
            renderItem,
            renderBeforeContent,
            searching,
            showChecksColumn,
            showFilterBox,
            showItemsWhileSearching,
            showTree,
            userFilteredItems
        } = this.props;

        let width = this.props.width;

        if (width === undefined && this.expandableContainer.current) {
            const minWidth = this.props.minCalloutWidth ?? 100;
            width = Math.max(this.expandableContainer.current.clientWidth, minWidth);
        }

        const { filteredItems, filterText, filteredSelection } = this.state;

        const calloutProps: IDropdownCalloutProps = {
            actions,
            anchorElement,
            anchorOffset,
            anchorOrigin,
            anchorPoint,
            ariaLabel,
            calloutContentClassName,
            columns,
            containerClassName,
            dropdownOrigin,
            filteredItems,
            filteredNoResultsText,
            selection: filteredSelection,
            filterPlaceholderText,
            filterText,
            getUnselectableRanges,
            id: dropdownId,
            items,
            loading,
            noItemsText,
            onActivate: this.onActivate,
            onFilterTextChanged: this.onFilterTextChanged,
            onDismiss: this.onDismiss,
            onSelect: this.onSelect,
            onToggle,
            portalProps,
            renderBeforeContent,
            renderItem,
            searching,
            showChecksColumn,
            showItemsWhileSearching,
            showFilterBox,
            showTree,
            updateFilteredItems: this.updateFilteredItems,
            userFilteredItems,
            width
        };
        return this.props.renderCallout!(calloutProps);
    };

    private updateFilteredItems = (): boolean => {
        updateFilteredItems(this.props, this.state);
        return true;
    };

    private debouncedUpdateFilteredItems = () => {
        updateFilteredItems(this.props, this.state);
    };
}

export function filterItemByText(filterText: string, item: IListBoxItem<{}>): boolean {
    if (item.text && item.type !== ListBoxItemType.Header && item.type !== ListBoxItemType.Divider && item.type !== ListBoxItemType.Loading) {
        return item.text.toLowerCase().indexOf(filterText.toLowerCase()) !== -1;
    }
    return false;
}

export function renderDropdownSelectedItemText(selection: IListSelection, items: IListBoxItem<{}>[]): string {
    const firstSelectedItem = items[selection.value[0].beginIndex];
    let text = (firstSelectedItem && firstSelectedItem.text) || "";
    if (selection.selectedCount > 1) {
        text = `${text} (+${selection.selectedCount - 1})`;
    }
    return text;
}

function updateFilteredItems<T = {}>(props: IDropdownProps<T>, state: IDropdownState<T>) {
    const { filteredSelection, filterText } = state;
    let filteredIndexMap: number[] = [];
    const items = getListBoxItemsValue(state.wrappedItems || props.items);
    let filteredItems = items;

    if (props.userFilteredItems) {
        filteredItems = getItemsValue(props.userFilteredItems);
        const userFilteredItemsIndexMap = props.userFilteredItemsIndexMap && props.userFilteredItemsIndexMap.value;
        if (userFilteredItemsIndexMap) {
            filteredIndexMap = userFilteredItemsIndexMap;
        } else {
            for (let filteredIndex = 0; filteredIndex < props.userFilteredItems.length; filteredIndex++) {
                const index = items.findIndex(listItem => listItem.id === filteredItems[filteredIndex].id);
                if ($DEBUG) {
                    if (index === -1) {
                        console.error(
                            "filteredItems contains an item not in items. " +
                                "Selection cannot be maintained unless filteredItems is a subset of items. " +
                                "Check item in filteredItems at index " +
                                filteredIndex
                        );
                    }
                }
                filteredIndexMap.push(index);
            }
        }
    }

    if (props.filterByText && filterText.value) {
        const filterItemsResults = filterItems<T>(filteredItems, filterText.value, filteredIndexMap, props.filterItem);
        filteredItems = filterItemsResults.filteredItems;
        filteredIndexMap = filterItemsResults.filteredIndexMap;
    }

    // Remove the first item if it's a divider
    while (filteredItems.length && filteredItems[0].type === ListBoxItemType.Divider) {
        filteredItems.shift();
        filteredIndexMap.shift();
    }

    if (!ObservableLike.getValue(props.searching) && !ObservableLike.getValue(props.loading) && state.expanded.value) {
        if (filterText.value) {
            let noResultsText = Resources.NoFilterResults;
            if (props.filteredNoResultsText) {
                noResultsText = ObservableLike.getValue(props.filteredNoResultsText);
            }

            Utils_Accessibility.announce(
                filteredItems.length > 0 ? format(Resources.AnnounceFilterResultCount, filteredItems.length) : noResultsText,
                true
            );
        } else if (filteredItems.length === 0 && props.noItemsText) {
            Utils_Accessibility.announce(props.noItemsText, true);
        } else if (filteredItems.length > 0) {
            Utils_Accessibility.announce(format(Resources.AnnounceItemCount, filteredItems.length));
        }
    }

    filteredSelection.updateFilteredSelection(filteredIndexMap, props.enforceSingleSelect ? false : undefined);
    state.filteredItems.value = filteredItems;
    return true;
}

export function filterItems<T = {}>(
    items: IListBoxItem<T>[],
    filterTextValue: string,
    currentFilteredIndexMap: number[] = [],
    filterItem: (filterText: string, item: IListBoxItem, items: IListBoxItem<T>[]) => boolean | number[] = filterItemByText
) {
    const filteredItems: IListBoxItem<T>[] = [];
    const filteredIndexMap: number[] = [];
    const filterMatches: number[][] = [];
    if (filterTextValue) {
        let lastHeader: IListBoxItem<T> | undefined;
        let lastHeaderIndex = -1;
        let lastDivider: IListBoxItem<T> | undefined;
        let lastDividerIndex = -1;
        for (let i = 0, l = items.length; i < l; i++) {
            const item = items[i];
            const itemIndex = currentFilteredIndexMap.length ? currentFilteredIndexMap[i] : i;

            // Add Dividers and Headers only if they have an item from their group showing.
            if (item.type === ListBoxItemType.Header) {
                lastHeader = item;
                lastHeaderIndex = itemIndex;
            } else if (item.type === ListBoxItemType.Divider) {
                lastDivider = item;
                lastDividerIndex = itemIndex;
            } else {
                const filterResults = filterItem(filterTextValue, item, items);
                if (filterResults || item.type === ListBoxItemType.Loading) {
                    // Add any divider, then header for this group
                    if (lastDivider && lastDivider.groupId === item.groupId) {
                        filteredItems.push(lastDivider);
                        filteredIndexMap.push(lastDividerIndex);
                        lastDivider = undefined;
                    }
                    if (lastHeader && lastHeader.groupId === item.groupId) {
                        filteredItems.push(lastHeader);
                        filteredIndexMap.push(lastHeaderIndex);
                        lastHeader = undefined;
                    }

                    filteredItems.push(item);
                    filteredIndexMap.push(itemIndex);

                    filterMatches.push(Array.isArray(filterResults) ? filterResults : []);
                }
            }
        }
    }

    return { filteredItems, filteredIndexMap, filterMatches };
}

/**
 * Filter the tree of items using user-entered text. Include all items with text matching
 * the filter and all their predecessors and descendants in the tree.
 * @returns items matching filter and all their predecessors and descendants in the tree, and the index of the first actual match (since we're returning predecessors)
 */
export function filterTreeItems<T = {}>(
    items: IListBoxItem<T>[],
    filterText: string,
    currentFilteredIndexMap: number[] = [],
    filterItem: (filterText: string, item: IListBoxItem, items: IListBoxItem<T>[]) => boolean | number[] = filterItemByText,
    filterMatchedItem: (item: IListBoxItem<T>) => boolean = filterMatchedItemByListboxType
): [IFilterResults<T>, number] {
    const filterResults = filterItems(items, filterText, currentFilteredIndexMap, filterItem);
    const filteredIndexes = filterResults.filteredIndexMap;

    // find the index of the first actual match to allow calling code to focus it
    const firstMatch = filterResults.filteredItems.find(filterMatchedItem);

    // reconstruct the list of filtered items, adding in descendants of filtered items
    const indexMap: { [key: number]: IListBoxItem<T> } = {};
    for (const index of filteredIndexes) {
        const item = items[index];
        let parent = item.parent;
        while (parent) {
            const parentIndex = items.indexOf(parent);
            indexMap[parentIndex] = parent;
            parent.expanded = true;
            parent = parent.parent;
        }

        indexMap[index] = item;
    }

    const filteredIndexMap: number[] = [];
    const filteredItems: IListBoxItem<T>[] = [];
    for (const indexStr of Object.keys(indexMap)) {
        const index = Number(indexStr);
        const value = indexMap[index];
        filteredIndexMap.push(index);
        filteredItems.push(value);
    }

    const firstMatchIndex = firstMatch ? items.indexOf(firstMatch) : -1;

    return [{ filteredIndexMap, filteredItems, filterMatches: [] }, firstMatchIndex];
}

export function filterMatchedItemByListboxType<T>(item: IListBoxItem<T>) {
    return !item.type || item.type === ListBoxItemType.Row;
}

export function renderHighlightedText(
    rowIndex: number,
    columnIndex: number,
    tableColumn: ITableColumn<IListBoxItem>,
    tableItem: IListBoxItem,
    filterResults?: number[]
): JSX.Element {
    let item: ISimpleListCell = tableItem;
    if (filterResults && tableItem.text) {
        item = { ...tableItem, textNode: getHighlightedText(tableItem.text, filterResults) };
    }

    return (
        <SimpleTableCell
            className={css(tableColumn.className, tableItem.className, tableItem.type === ListBoxItemType.Header && "bolt-list-box-header")}
            columnIndex={columnIndex}
            key={columnIndex}
            tableColumn={tableColumn}
        >
            <div
                id={tableItem.type === ListBoxItemType.Header ? `header-${tableItem.id}` : undefined}
                aria-label={tableItem.type === ListBoxItemType.Header ? format(Resources.HeaderAriaLabel, tableItem.text) : undefined}
            >
                {renderListCell(item)}
            </div>
        </SimpleTableCell>
    );
}

export function getHighlightedText(text: string, matchingIndices: number[], className?: string): JSX.Element {
    const splitText: { text: string; bold: boolean }[] = [];
    let splitTextIndex = -1;

    // Split text into bold and non-bold sections
    for (let i = 0; i < text.length; i++) {
        if (matchingIndices.indexOf(i) !== -1) {
            if (splitText && splitText[splitText.length - 1] && splitText[splitText.length - 1].bold) {
                splitText[splitTextIndex].text += text.charAt(i);
            } else {
                splitText[++splitTextIndex] = { text: text.charAt(i), bold: true };
            }
        } else {
            if (splitText && splitText[splitText.length - 1] && !splitText[splitText.length - 1].bold) {
                splitText[splitTextIndex].text += text.charAt(i);
            } else {
                splitText[++splitTextIndex] = { text: text.charAt(i), bold: false };
            }
        }
    }

    const formattedText: (JSX.Element | string)[] = [];
    for (let i = 0; i < splitText.length; i++) {
        const substring = splitText[i];
        substring.bold
            ? formattedText.push(
                  <span className="font-weight-heavy" key={`${text}-${i}`}>
                      {substring.text}
                  </span>
              )
            : formattedText.push(substring.text);
    }

    return <span className={className}>{formattedText}</span>;
}
